import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import API from "../utils/api";

const VITE_API = process.env.REACT_APP_API_URL || "";

function todayIST() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}
function thisMonthIST() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }).slice(0, 7);
}
function getMonthRange(month) {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return { start: `${month}-01`, end: `${month}-${String(last).padStart(2, "0")}` };
}
function fmtTime(dt) {
  if (!dt) return "—";
  const raw = typeof dt === "string" && !dt.endsWith("Z") && !dt.includes("+") ? dt + "Z" : dt;
  return new Date(raw).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata",
  });
}
function fmtDateShort(ds) {
  if (!ds) return ds;
  const [, , d] = ds.split("-");
  return d;
}
function fmtDateLong(ds) {
  if (!ds) return ds;
  try {
    return new Date(ds + "T00:00:00").toLocaleDateString("en-IN", {
      day: "numeric", month: "short", weekday: "short",
    });
  } catch { return ds; }
}
function barColor(pct) {
  if (pct >= 80) return "#22c55e";
  if (pct >= 40) return "#eab308";
  return "#ef4444";
}
function coverageColors(pct, noTasks) {
  if (noTasks) return { bg: "#fef2f2", border: "#fca5a5", text: "#dc2626" };
  if (pct >= 80) return { bg: "#f0fdf4", border: "#86efac", text: "#15803d" };
  if (pct >= 40) return { bg: "#fefce8", border: "#fde68a", text: "#92400e" };
  return { bg: "#fef2f2", border: "#fca5a5", text: "#dc2626" };
}
const TYPE_LABELS = {
  office: "WFO", wfh: "WFH", institution_visit: "Field", exam_duty: "Exam",
  official_travel: "Travel", planned_leave: "Leave", auto_leave: "Leave",
};
const TYPE_COLORS = {
  office: "#1d4ed8", wfh: "#7c3aed", institution_visit: "#0891b2",
  exam_duty: "#d97706", official_travel: "#0369a1", planned_leave: "#64748b", auto_leave: "#94a3b8",
};
// Per-type config for field-plan rows (institution_visit, exam_duty, official_travel)
const FIELD_CONFIG = {
  institution_visit: {
    bg: "#fffbeb", border: "#fcd34d",
    venueIcon: "🏫", venueLabel: "Institutions",
    timeLabel: "Hours on Field", sectionTitle: "Field Visit Metrics",
    productivityLabel: "Field productivity (vs 7h day)",
  },
  exam_duty: {
    bg: "#faf5ff", border: "#c4b5fd",
    venueIcon: "🏛️", venueLabel: "Exam Venues",
    timeLabel: "Hours on Duty", sectionTitle: "Exam Duty Metrics",
    productivityLabel: "Duty productivity (vs 7h day)",
  },
  official_travel: {
    bg: "#e0f2fe", border: "#7dd3fc",
    venueIcon: "🗺️", venueLabel: "Stops",
    timeLabel: "Hours Travelled", sectionTitle: "Travel Metrics",
    productivityLabel: "Travel productivity (vs 7h day)",
  },
};

// ─── Bar Chart ─────────────────────────────────────────────────────────────────
function BarChart({ days, avgCoverage }) {
  const [hovered, setHovered] = useState(null);
  const MAX_H = 140;

  if (!days || !days.length) {
    return (
      <div style={{ textAlign: "center", padding: 32, color: "#94a3b8", fontSize: 13 }}>
        No data for this period.
      </div>
    );
  }

  const gridLines = [100, 75, 50, 25];

  return (
    <div style={{ position: "relative" }}>
      {/* Chart area */}
      <div style={{ position: "relative", height: MAX_H + 36, paddingLeft: 32 }}>
        {/* Y gridlines */}
        {gridLines.map(g => (
          <div key={g} style={{
            position: "absolute", left: 32, right: 0,
            top: `${Math.round((100 - g) / 100 * (MAX_H - 8))}px`,
            borderTop: "1px dashed #e2e8f0", display: "flex", alignItems: "center",
          }}>
            <span style={{ position: "absolute", left: -28, fontSize: 9, color: "#94a3b8", lineHeight: 1, top: -5 }}>
              {g}%
            </span>
          </div>
        ))}
        {/* Avg line */}
        {avgCoverage > 0 && (
          <div style={{
            position: "absolute", left: 32, right: 0,
            top: `${Math.round((100 - avgCoverage) / 100 * (MAX_H - 8))}px`,
            borderTop: "2px dashed #6366f1", zIndex: 5,
          }}>
            <span style={{ position: "absolute", right: 4, top: -14, fontSize: 9, color: "#6366f1", fontWeight: 800, background: "#fff", padding: "1px 4px", borderRadius: 4 }}>
              avg {avgCoverage}%
            </span>
          </div>
        )}
        {/* Bars */}
        <div style={{
          display: "flex", gap: days.length > 25 ? 2 : 5,
          alignItems: "flex-end", height: MAX_H, paddingRight: 8,
          position: "relative", zIndex: 3,
        }}>
          {days.map((d, i) => {
            const barH = d.checkedIn === 0 ? 3 : Math.max(3, Math.round(d.avgCoverage / 100 * (MAX_H - 8)));
            const isHov = hovered === i;
            const minW = days.length > 25 ? 8 : days.length > 15 ? 14 : 20;
            return (
              <div
                key={d.date}
                style={{ flex: "1 1 0", minWidth: minW, maxWidth: 44, display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", position: "relative" }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                {/* Tooltip */}
                {isHov && (
                  <div style={{
                    position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
                    background: "#1e293b", color: "#fff", borderRadius: 8, padding: "8px 12px",
                    fontSize: 11, whiteSpace: "nowrap", zIndex: 100,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)", lineHeight: 1.6,
                  }}>
                    <strong style={{ display: "block", marginBottom: 2 }}>{fmtDateLong(d.date)}</strong>
                    Avg coverage: <strong style={{ color: barColor(d.avgCoverage) }}>{d.avgCoverage}%</strong><br />
                    Checked in: {d.checkedIn}{d.total ? `/${d.total}` : ""}<br />
                    {d.good > 0 && <>Good: {d.good} · </>}
                    {d.partial > 0 && <>Partial: {d.partial} · </>}
                    {d.low > 0 && <>Low: {d.low}</>}
                    {d.noTasks > 0 && <><br />No tasks: {d.noTasks}</>}
                  </div>
                )}
                <div style={{
                  width: "100%", height: barH,
                  background: d.checkedIn === 0 ? "#e2e8f0" : barColor(d.avgCoverage),
                  borderRadius: "4px 4px 0 0",
                  opacity: isHov ? 1 : 0.82,
                  transform: isHov ? "scaleY(1.05)" : "scaleY(1)",
                  transformOrigin: "bottom",
                  transition: "all 0.12s",
                  boxShadow: isHov ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                }} />
              </div>
            );
          })}
        </div>
        {/* X-axis labels */}
        <div style={{ display: "flex", gap: days.length > 25 ? 2 : 5, paddingRight: 8, marginTop: 5 }}>
          {days.map((d, i) => (
            <div key={d.date} style={{
              flex: "1 1 0", minWidth: days.length > 25 ? 8 : 14, maxWidth: 44,
              textAlign: "center", fontSize: 9, color: "#94a3b8",
            }}>
              {days.length <= 31
                ? fmtDateShort(d.date)
                : (i % Math.ceil(days.length / 15) === 0 ? d.date.slice(5) : "")}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
export default function DailyProductivity() {
  const { user } = useAuth();
  const navigate  = useNavigate();
  const effectiveRole = user?.role === "superadmin" ? "superadmin" : (user?.app_role_overrides?.crm || user?.role);
  const isEmployee = effectiveRole === "employee";
  const canManage  = ["manager", "admin", "superadmin", "sales_admin", "team_leader"].includes(effectiveRole);
  const color = user?.company === "ebekmed" ? "#079b9b" : "#ef1f35";

  /* ── View mode ─────────────────────────────────────────────── */
  const [viewMode, setViewMode] = useState("daily"); // daily | monthly | range

  /* ── Shared filters ─────────────────────────────────────────── */
  const [dept,    setDept]    = useState("");
  const [depts,   setDepts]   = useState([]);
  const [empId,   setEmpId]   = useState("");
  const [empList, setEmpList] = useState([]); // [{ id, name }]

  /* ── Daily state ────────────────────────────────────────────── */
  const [date,      setDate]      = useState(todayIST());
  const [dailyRows, setDailyRows] = useState([]);
  const [expanded,  setExpanded]  = useState({});

  /* ── Monthly / Range state ──────────────────────────────────── */
  const [month,     setMonth]     = useState(thisMonthIST());
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 6);
    return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  });
  const [endDate, setEndDate] = useState(todayIST());
  const [rangeData, setRangeData] = useState({ days: [] });

  /* ── Summary (yearly matrix) state ─────────────────────────── */
  const [summaryYear, setSummaryYear] = useState(new Date().getFullYear());
  const [summaryData, setSummaryData] = useState({ employees: [] });

  /* ── Loading / Error ────────────────────────────────────────── */
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [dlLoading, setDlLoading] = useState(false);

  /* ── Init: pre-load employee list for managers/admins ──────── */
  useEffect(() => {
    if (!canManage) return;
    API.get(`/daily-tasks/productivity?date=${todayIST()}`)
      .then(res => {
        const rows = res.data?.rows || [];
        const emps = [...new Map(rows.map(r => [r.user_id, { id: r.user_id, name: r.employee_name }])).values()]
          .sort((a, b) => a.name.localeCompare(b.name));
        setEmpList(emps);
        setDepts([...new Set(rows.map(r => r.department).filter(Boolean))].sort());
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  /* ── Loaders ─────────────────────────────────────────────────── */
  const loadDaily = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ date });
      if (dept)  params.set("department", dept);
      if (empId) params.set("user_id", empId);
      const res = await API.get(`/daily-tasks/productivity?${params}`);
      setDailyRows(res.data?.rows || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load data");
    } finally { setLoading(false); }
  }, [date, dept, empId]);

  const loadRange = useCallback(async (start, end) => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ start_date: start, end_date: end });
      if (dept)  params.set("department", dept);
      if (empId) params.set("user_id", empId);
      const res = await API.get(`/daily-tasks/productivity/range?${params}`);
      setRangeData(res.data || { days: [] });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load data");
    } finally { setLoading(false); }
  }, [dept, empId]);

  const loadSummary = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ year: summaryYear });
      if (dept) params.set("department", dept);
      const res = await API.get(`/daily-tasks/productivity/summary?${params}`);
      setSummaryData(res.data || { employees: [] });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load summary");
    } finally { setLoading(false); }
  }, [summaryYear, dept]);

  const { start: mStart, end: mEnd } = useMemo(() => getMonthRange(month), [month]);

  useEffect(() => {
    if (viewMode === "daily") loadDaily();
    else if (viewMode === "monthly") loadRange(mStart, mEnd);
    else if (viewMode === "summary") loadSummary();
    else if (startDate && endDate && startDate <= endDate) loadRange(startDate, endDate);
  }, [viewMode, loadDaily, loadRange, loadSummary, mStart, mEnd, startDate, endDate]);

  /* ── Derived daily stats ──────────────────────────────────────── */
  const filteredDaily = dailyRows.filter(r => {
    if (dept  && r.department !== dept) return false;
    if (empId && String(r.user_id) !== String(empId)) return false;
    return true;
  });
  const totalD     = filteredDaily.length;
  const checkedInD = filteredDaily.filter(r => r.check_in_time).length;
  const goodD      = filteredDaily.filter(r => r.coverage_status === "good").length;
  const noTasksD   = filteredDaily.filter(r => !r.tasks_logged).length;

  /* ── Derived range stats ─────────────────────────────────────── */
  const days         = rangeData.days || [];
  const avgCoverage  = days.length ? Math.round(days.reduce((s, d) => s + d.avgCoverage, 0) / days.length) : 0;
  const totalCheckIns = days.reduce((s, d) => s + d.checkedIn, 0);
  const goodDays     = days.filter(d => d.avgCoverage >= 80).length;
  const totalNoTasks = days.reduce((s, d) => s + d.noTasks, 0);

  /* ── Download ─────────────────────────────────────────────────── */
  const handleDownload = async (dlStart, dlEnd) => {
    setDlLoading(true);
    try {
      const token = localStorage.getItem("crm_token") || localStorage.getItem("token");
      const params = dlStart === dlEnd
        ? new URLSearchParams({ date: dlStart })
        : new URLSearchParams({ date_from: dlStart, date_to: dlEnd });
      if (dept)  params.set("department", dept);
      if (empId) params.set("user_id", empId);
      const url = `${VITE_API}/api/daily-tasks/productivity/download?${params}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `productivity_${dlStart}${dlStart !== dlEnd ? "_to_" + dlEnd : ""}.xlsx`;
      a.click();
    } catch (err) { alert(err.message); }
    finally { setDlLoading(false); }
  };

  /* ── Inline style helpers ─────────────────────────────────────── */
  const st = {
    page:      { padding: "20px 24px", maxWidth: 1100, margin: "0 auto", fontFamily: "inherit" },
    hRow:      { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 20 },
    h2:        { margin: 0, fontSize: 20, fontWeight: 800, color: "#1e293b" },
    sub:       { margin: "4px 0 0", fontSize: 13, color: "#64748b" },
    tabs:      { display: "flex", gap: 4, background: "#f1f5f9", borderRadius: 10, padding: 4, marginBottom: 20 },
    tab: (on) => ({
      flex: 1, padding: "8px 14px", borderRadius: 7, border: "none", cursor: "pointer",
      fontSize: 13, fontWeight: on ? 700 : 500,
      background: on ? "#fff" : "transparent",
      color: on ? color : "#64748b",
      boxShadow: on ? "0 1px 4px rgba(0,0,0,0.09)" : "none",
      transition: "all 0.15s",
    }),
    filters:   { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20, alignItems: "flex-end" },
    fGroup:    { display: "flex", flexDirection: "column", gap: 4 },
    fLabel:    { fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" },
    fInput:    { padding: "7px 10px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "#fff" },
    cards:     { display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 },
    card: (c) => ({ background: "#fff", border: "1px solid #e2e8f0", borderTop: `3px solid ${c}`, borderRadius: 12, padding: "14px 20px", flex: "1 1 110px" }),
    cardVal: (c) => ({ fontSize: 24, fontWeight: 800, color: c }),
    cardLbl:   { fontSize: 12, color: "#64748b", marginTop: 2 },
    btn: (dis) => ({
      background: dis ? "#e2e8f0" : color, color: dis ? "#94a3b8" : "#fff",
      border: "none", borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
      cursor: dis ? "not-allowed" : "pointer",
    }),
    refreshBtn:{ padding: "8px 14px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13, background: "#f8fafc", cursor: "pointer", fontFamily: "inherit" },
    chartBox:  { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px", marginBottom: 20 },
    chartTitle:{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 14 },
    tableBox:  { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px" },
    th:        { padding: "6px 12px", textAlign: "left", fontWeight: 700, color: "#475569", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap", fontSize: 12 },
    td:        { padding: "7px 12px", borderBottom: "1px solid #f1f5f9", color: "#374151", fontSize: 12 },
    error:     { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: 12, color: "#dc2626", marginBottom: 16 },
    empty:     { textAlign: "center", padding: 48, color: "#94a3b8" },
    loading:   { textAlign: "center", padding: 40, color: "#64748b" },
    legend:    { display: "flex", gap: 14, marginTop: 14, fontSize: 11, color: "#64748b", flexWrap: "wrap" },
  };

  /* ── Dept filter (shared) ─────────────────────────────────────── */
  const DeptFilter = () => canManage && depts.length > 0 ? (
    <div style={st.fGroup}>
      <span style={st.fLabel}>Department</span>
      <select value={dept} onChange={e => setDept(e.target.value)} style={st.fInput}>
        <option value="">All Departments</option>
        {depts.map(d => <option key={d} value={d}>{d}</option>)}
      </select>
    </div>
  ) : null;

  /* ── Employee filter (shared, managers/admins/superadmins only) ── */
  const EmpFilter = () => canManage && empList.length > 0 ? (
    <div style={st.fGroup}>
      <span style={st.fLabel}>Employee</span>
      <select value={empId} onChange={e => setEmpId(e.target.value)} style={{ ...st.fInput, minWidth: 160 }}>
        <option value="">All Employees</option>
        {empList.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
      </select>
    </div>
  ) : null;

  /* ── Daily view ───────────────────────────────────────────────── */
  const DailyView = () => (
    <>
      <div style={st.filters}>
        <div style={st.fGroup}>
          <span style={st.fLabel}>Date</span>
          <input type="date" value={date} max={todayIST()} onChange={e => setDate(e.target.value)} style={st.fInput} />
        </div>
        <DeptFilter />
        <EmpFilter />
        <button style={st.refreshBtn} onClick={loadDaily}>🔄 Refresh</button>
        {canManage && (
          <button style={st.btn(dlLoading || !dailyRows.length)} onClick={() => handleDownload(date, date)} disabled={dlLoading || !dailyRows.length}>
            {dlLoading ? "Downloading…" : "⬇ Download Excel"}
          </button>
        )}
      </div>

      {canManage && (
        <div style={st.cards}>
          {[[totalD, "Total Members", "#1e293b"], [checkedInD, "Checked In", color], [goodD, "Good Coverage ≥80%", "#15803d"], [noTasksD, "No Tasks Logged", "#dc2626"]].map(([v, l, c]) => (
            <div key={l} style={st.card(c)}>
              <div style={st.cardVal(c)}>{v}</div>
              <div style={st.cardLbl}>{l}</div>
            </div>
          ))}
        </div>
      )}

      {loading  && <div style={st.loading}>Loading…</div>}
      {error    && <div style={st.error}>{error}</div>}
      {!loading && !error && filteredDaily.length === 0 && (
        <div style={st.empty}>No attendance records for {date}{dept ? ` in ${dept}` : ""}.</div>
      )}

      {!loading && filteredDaily.map(row => {
        const isExp        = expanded[row.user_id];
        const isLeave      = ["planned_leave","auto_leave"].includes(row.plan_type);
        const isFieldVisit = ["institution_visit", "exam_duty", "official_travel"].includes(row.plan_type);
        // Field visit productivity: login_hours vs 7h productive day
        const fieldPct     = isFieldVisit ? Math.min(100, Math.round((row.login_hours / 7) * 100)) : 0;
        const fieldCfg     = isFieldVisit ? (FIELD_CONFIG[row.plan_type] || FIELD_CONFIG.institution_visit) : null;
        const cc           = isLeave
          ? { bg: "#f8fafc", border: "#e2e8f0", text: "#64748b" }
          : isFieldVisit
            ? { bg: fieldCfg.bg, border: fieldCfg.border, text: fieldPct >= 80 ? "#15803d" : fieldPct >= 50 ? "#d97706" : "#dc2626" }
            : coverageColors(row.coverage_pct, !row.tasks_logged);
        return (
          <div key={row.checkin_id || row.user_id} style={{ background: "#fff", border: `1px solid ${cc.border}`, borderRadius: 12, marginBottom: 10, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <div
              style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 16px", cursor: "pointer", background: cc.bg }}
              onClick={() => setExpanded(p => ({ ...p, [row.user_id]: !p[row.user_id] }))}
            >
              {canManage && (
                <div style={{ flex: "2 1 160px", minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.employee_name || "—"}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>{row.email}{row.department ? ` · ${row.department}` : ""}</div>
                </div>
              )}
              <span style={{ background: TYPE_COLORS[row.plan_type] || "#475569", color: "#fff", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                {TYPE_LABELS[row.plan_type] || row.plan_type}
              </span>
              {!isLeave && <>
                <div style={{ flex: "1 1 130px", fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                  In: <strong>{fmtTime(row.check_in_time)}</strong> · Out: <strong>{fmtTime(row.check_out_time)}</strong>
                </div>
                {isFieldVisit ? (
                  <>
                    <div style={{ flex: "1 1 160px", fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                      {fieldCfg.venueIcon} <strong>{row.plan_type === "official_travel" && !row.visit_count ? "—" : (row.visit_count || 0)}</strong> {fieldCfg.venueLabel.toLowerCase()} · 📍 <strong>{row.total_km_travelled > 0 ? `${parseFloat(row.total_km_travelled).toFixed(1)} km` : "–"}</strong>
                    </div>
                    <div style={{ flexShrink: 0, minWidth: 80, textAlign: "right" }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: cc.text }}>{fieldPct}%</div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>field time</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ flex: "1 1 130px", fontSize: 12, color: "#475569", whiteSpace: "nowrap" }}>
                      Login: <strong>{row.login_hours}h</strong> · Tasks: <strong>{row.task_hours}h</strong>
                    </div>
                    <div style={{ flexShrink: 0, minWidth: 80, textAlign: "right" }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: cc.text }}>{row.tasks_logged ? `${row.coverage_pct}%` : "No Tasks"}</div>
                      <div style={{ fontSize: 10, color: "#94a3b8" }}>coverage</div>
                    </div>
                  </>
                )}
              </>}
              <span style={{ color: "#94a3b8", fontSize: 12, flexShrink: 0 }}>{isExp ? "▲" : "▼"}</span>
            </div>
            {isExp && !isLeave && (
              <div style={{ padding: "10px 16px 14px", borderTop: "1px solid #f1f5f9" }}>
                {isFieldVisit ? (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 10, textTransform: "uppercase" }}>
                      {fieldCfg.sectionTitle}
                    </div>
                    <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                      {[
                        { icon: "🕐", label: fieldCfg.timeLabel,  value: `${row.login_hours}h`, color: "#0284c7" },
                        { icon: "📍", label: "Route KM",           value: row.total_km_travelled > 0 ? `${parseFloat(row.total_km_travelled).toFixed(1)} km` : "–", color: "#7c3aed" },
                        { icon: fieldCfg.venueIcon, label: fieldCfg.venueLabel, value: row.plan_type === "official_travel" && !row.visit_count ? "—" : (row.visit_count || 0), color: "#d97706" },
                      ].map(s => (
                        <div key={s.label} style={{ flex: 1, minWidth: 90, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
                          <div style={{ fontSize: 18, marginBottom: 2 }}>{s.icon}</div>
                          <div style={{ fontSize: 17, fontWeight: 700, color: s.color }}>{s.value}</div>
                          <div style={{ fontSize: 11, color: "#78716c", marginTop: 2 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                      <span>{fieldCfg.productivityLabel}</span>
                      <span style={{ fontWeight: 700, color: cc.text }}>{fieldPct}%</span>
                    </div>
                    <div style={{ background: "#e2e8f0", borderRadius: 99, height: 7 }}>
                      <div style={{ background: barColor(fieldPct), borderRadius: 99, height: 7, width: `${fieldPct}%`, transition: "width 0.4s" }} />
                    </div>
                  </div>
                ) : row.tasks.length === 0 ? (
                  <div style={{ padding: "10px 14px", background: "#fef2f2", borderRadius: 8, color: "#dc2626", fontSize: 13, fontWeight: 600 }}>
                    ⚠ No tasks logged for this day.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 8, textTransform: "uppercase" }}>
                      Tasks — {row.tasks.length} {row.tasks.length === 1 ? "entry" : "entries"}, {row.task_hours}h total
                    </div>
                    {row.tasks.map((t, i) => (
                      <div key={t.id || i} style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8fafc", borderRadius: 8, padding: "8px 12px", border: "1px solid #e2e8f0", flexWrap: "wrap", marginBottom: 6 }}>
                        <div style={{ flex: "1 1 200px", fontSize: 13, color: "#1e293b", fontWeight: 600 }}>{t.task_name}</div>
                        <div style={{ fontSize: 12, color: "#64748b" }}>
                          {t.from_time && t.to_time ? `${t.from_time} → ${t.to_time}` : t.from_time || t.to_time || ""}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: color, background: "#fef9f9", border: `1px solid ${color}33`, borderRadius: 6, padding: "2px 8px", flexShrink: 0 }}>
                          {Number(t.hours_spent || 0).toFixed(2)} hrs
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginBottom: 5 }}>
                        <span>Task coverage vs login time</span>
                        <span style={{ fontWeight: 700, color: cc.text }}>{row.coverage_pct}%</span>
                      </div>
                      <div style={{ background: "#e2e8f0", borderRadius: 99, height: 7 }}>
                        <div style={{ background: barColor(row.coverage_pct), borderRadius: 99, height: 7, width: `${Math.min(100, row.coverage_pct)}%`, transition: "width 0.4s" }} />
                      </div>
                      {row.login_hours > 0 && row.task_hours < row.login_hours * 0.8 && (
                        <div style={{ fontSize: 11, color: "#92400e", marginTop: 5 }}>
                          {Number((row.login_hours - row.task_hours).toFixed(2))}h unaccounted — task log incomplete
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  /* ── Range / Monthly view ─────────────────────────────────────── */
  const RangeView = ({ isMonthly }) => {
    const dlStart = isMonthly ? mStart : startDate;
    const dlEnd   = isMonthly ? mEnd   : endDate;

    const refresh = () => isMonthly ? loadRange(mStart, mEnd) : loadRange(startDate, endDate);

    const monthLabel = isMonthly
      ? new Date(month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" })
      : null;

    return (
      <>
        {/* Filters */}
        <div style={st.filters}>
          {isMonthly ? (
            <div style={st.fGroup}>
              <span style={st.fLabel}>Month</span>
              <input type="month" value={month} max={thisMonthIST()} onChange={e => setMonth(e.target.value)} style={st.fInput} />
            </div>
          ) : (
            <>
              <div style={st.fGroup}>
                <span style={st.fLabel}>From Date</span>
                <input type="date" value={startDate} max={endDate} onChange={e => setStartDate(e.target.value)} style={st.fInput} />
              </div>
              <div style={st.fGroup}>
                <span style={st.fLabel}>To Date</span>
                <input type="date" value={endDate} max={todayIST()} min={startDate} onChange={e => setEndDate(e.target.value)} style={st.fInput} />
              </div>
            </>
          )}
          <DeptFilter />
          <EmpFilter />
          <button style={st.refreshBtn} onClick={refresh}>🔄 Refresh</button>
          {canManage && (
            <button style={st.btn(dlLoading || !days.length)} onClick={() => handleDownload(dlStart, dlEnd)} disabled={dlLoading || !days.length}>
              {dlLoading ? "Downloading…" : "⬇ Download Excel"}
            </button>
          )}
        </div>

        {/* Summary cards */}
        <div style={st.cards}>
          {[
            [avgCoverage + "%", "Avg Productivity", "#6366f1"],
            [totalCheckIns,     "Total Check-ins",  color],
            [goodDays,          "Days ≥80% Coverage","#15803d"],
            [totalNoTasks,      "No-Task Check-ins", "#dc2626"],
          ].map(([v, l, c]) => (
            <div key={l} style={st.card(c)}>
              <div style={st.cardVal(c)}>{v}</div>
              <div style={st.cardLbl}>{l}</div>
            </div>
          ))}
        </div>

        {loading && <div style={st.loading}>Loading…</div>}
        {error   && <div style={st.error}>{error}</div>}

        {!loading && (
          <>
            {/* Bar chart */}
            <div style={st.chartBox}>
              <div style={st.chartTitle}>
                📊 {isMonthly ? `Coverage by Day — ${monthLabel}` : `Coverage by Day — ${startDate} to ${endDate}`}
              </div>
              <BarChart days={days} avgCoverage={avgCoverage} />
              {/* Legend */}
              <div style={st.legend}>
                {[["#22c55e","Good ≥80%"],["#eab308","Partial 40–79%"],["#ef4444","Low <40%"],["#e2e8f0","No check-in"]].map(([c, l]) => (
                  <span key={l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: "inline-block", flexShrink: 0 }} />
                    {l}
                  </span>
                ))}
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ width: 14, height: 2, background: "#6366f1", display: "inline-block", flexShrink: 0 }} />
                  Average coverage
                </span>
              </div>
            </div>

            {/* Daily breakdown table */}
            {days.length > 0 && (
              <div style={st.tableBox}>
                <div style={st.chartTitle}>📋 Daily Breakdown</div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Date", "Checked In", "Avg Coverage", "Good", "Partial", "Low", "No Tasks"].map(h => (
                          <th key={h} style={st.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {days.map(d => (
                        <tr key={d.date}>
                          <td style={{ ...st.td, fontWeight: 600 }}>{fmtDateLong(d.date)}</td>
                          <td style={st.td}>{d.checkedIn}{canManage && d.total ? `/${d.total}` : ""}</td>
                          <td style={st.td}>
                            <span style={{ fontWeight: 700, color: d.checkedIn === 0 ? "#94a3b8" : barColor(d.avgCoverage) }}>
                              {d.checkedIn === 0 ? "—" : `${d.avgCoverage}%`}
                            </span>
                          </td>
                          <td style={{ ...st.td, color: "#15803d", fontWeight: d.good > 0 ? 700 : 400 }}>{d.good || "—"}</td>
                          <td style={{ ...st.td, color: "#92400e", fontWeight: d.partial > 0 ? 700 : 400 }}>{d.partial || "—"}</td>
                          <td style={{ ...st.td, color: "#dc2626", fontWeight: d.low > 0 ? 700 : 400 }}>{d.low || "—"}</td>
                          <td style={{ ...st.td, color: d.noTasks > 0 ? "#dc2626" : "#94a3b8", fontWeight: d.noTasks > 0 ? 700 : 400 }}>{d.noTasks || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {days.length === 0 && !loading && !error && (
              <div style={st.empty}>No data for the selected period.</div>
            )}
          </>
        )}
      </>
    );
  };

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div style={st.page}>
      {/* Header */}
      <div style={st.hRow}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <button
            onClick={() => navigate(-1)}
            title="Back"
            style={{
              marginTop: 2,
              background: "#f1f5f9", border: "1px solid #e2e8f0",
              borderRadius: 8, width: 34, height: 34,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", color: "#475569", fontSize: 16, flexShrink: 0,
            }}
          >←</button>
          <div>
            <h2 style={st.h2}>Daily Productivity</h2>
            <p style={st.sub}>
              {isEmployee
                ? "Track your task coverage vs login time"
                : empId
                  ? `Viewing: ${empList.find(e => String(e.id) === String(empId))?.name || "Employee"} — task coverage vs login time`
                  : "Team productivity analytics — login time vs task timelines"}
            </p>
          </div>
        </div>
      </div>

      {/* View mode tabs */}
      <div style={st.tabs}>
        {[
          ["daily",   "📅  Daily"],
          ["monthly", "📆  Monthly"],
          ["range",   "📊  Custom Range"],
          ...(canManage ? [["summary","🏆  Yearly Summary"]] : []),
        ].map(([mode, label]) => (
          <button key={mode} style={st.tab(viewMode === mode)} onClick={() => setViewMode(mode)}>
            {label}
          </button>
        ))}
      </div>

      {viewMode === "daily"   && <DailyView />}
      {viewMode === "monthly" && <RangeView isMonthly={true} />}
      {viewMode === "range"   && <RangeView isMonthly={false} />}
      {viewMode === "summary" && canManage && (
        <CRMSummaryView
          summaryYear={summaryYear}
          setSummaryYear={setSummaryYear}
          summaryData={summaryData}
          loading={loading}
          dept={dept}
          setDept={setDept}
          depts={depts}
        />
      )}
    </div>
  );
}

// ─── CRM Yearly Productivity Summary View ─────────────────────────────────────
function CRMSummaryView({ summaryYear, setSummaryYear, summaryData, loading, dept, setDept, depts }) {
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const employees = summaryData?.employees || [];

  function cellColor(v) {
    if (v === null || v === undefined) return { bg: "#f8fafc", color: "#94a3b8" };
    if (v >= 80) return { bg: "#dcfce7", color: "#166534" };
    if (v >= 50) return { bg: "#fef9c3", color: "#854d0e" };
    if (v >= 1)  return { bg: "#fee2e2", color: "#991b1b" };
    return { bg: "#f8fafc", color: "#94a3b8" };
  }

  return (
    <div>
      <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:20, alignItems:"center" }}>
        <label style={{ fontWeight:600, color:"#374151" }}>Year:</label>
        <select value={summaryYear} onChange={e => setSummaryYear(Number(e.target.value))}
          style={{ padding:"6px 12px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:14 }}>
          {[2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        {depts.length > 0 && (
          <>
            <label style={{ fontWeight:600, color:"#374151" }}>Dept:</label>
            <select value={dept} onChange={e => setDept(e.target.value)}
              style={{ padding:"6px 12px", borderRadius:8, border:"1px solid #e5e7eb", fontSize:14 }}>
              <option value="">All Departments</option>
              {depts.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </>
        )}
        <div style={{ marginLeft:"auto", fontSize:12, color:"#6b7280" }}>
          Task productivity = logged task hours ÷ 7h × 100
        </div>
      </div>

      {loading && <div style={{ textAlign:"center", padding:40, color:"#6b7280" }}>Loading…</div>}
      {!loading && employees.length === 0 && (
        <div style={{ textAlign:"center", padding:40, color:"#6b7280" }}>No data for {summaryYear}</div>
      )}
      {!loading && employees.length > 0 && (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ background:"#f1f5f9" }}>
                <th style={{ padding:"10px 12px", textAlign:"left", fontWeight:700, color:"#374151", borderBottom:"2px solid #e2e8f0", minWidth:140, position:"sticky", left:0, background:"#f1f5f9", zIndex:1 }}>Employee</th>
                <th style={{ padding:"10px 8px", textAlign:"left", fontWeight:600, color:"#6b7280", borderBottom:"2px solid #e2e8f0", minWidth:100 }}>Department</th>
                {MONTHS.map(m => (
                  <th key={m} style={{ padding:"10px 6px", textAlign:"center", fontWeight:600, color:"#6b7280", borderBottom:"2px solid #e2e8f0", minWidth:56 }}>{m}</th>
                ))}
                <th style={{ padding:"10px 10px", textAlign:"center", fontWeight:700, color:"#374151", borderBottom:"2px solid #e2e8f0", minWidth:70, background:"#e2e8f0" }}>YTD Avg</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp, idx) => (
                <tr key={emp.id || idx} style={{ background: idx%2===0 ? "#fff" : "#fafafa" }}>
                  <td style={{ padding:"8px 12px", fontWeight:600, color:"#1e293b", borderBottom:"1px solid #f1f5f9", position:"sticky", left:0, background: idx%2===0 ? "#fff" : "#fafafa", zIndex:1 }}>{emp.name}</td>
                  <td style={{ padding:"8px 8px", color:"#6b7280", borderBottom:"1px solid #f1f5f9" }}>{emp.department || "—"}</td>
                  {MONTHS.map((_, i) => {
                    const mData = emp.months[i+1];
                    const val = mData?.avg_productivity;
                    const c = cellColor(val);
                    return (
                      <td key={i} title={mData ? `${mData.working_days} working days` : "No data"}
                        style={{ padding:"6px 4px", textAlign:"center", borderBottom:"1px solid #f1f5f9", fontWeight:600, fontSize:12, ...c }}>
                        {val != null ? `${Math.round(val)}%` : "—"}
                      </td>
                    );
                  })}
                  <td style={{ padding:"6px 10px", textAlign:"center", borderBottom:"1px solid #f1f5f9", fontWeight:700, fontSize:13, ...cellColor(emp.ytd_avg) }}>
                    {emp.ytd_avg > 0 ? `${emp.ytd_avg}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background:"#f1f5f9", fontWeight:700 }}>
                <td colSpan={2} style={{ padding:"10px 12px", color:"#374151", fontSize:13, borderTop:"2px solid #e2e8f0" }}>Team Average</td>
                {MONTHS.map((_, i) => {
                  const vals = employees.map(e => e.months[i+1]?.avg_productivity).filter(v => v != null && v > 0);
                  const avg = vals.length ? Math.round(vals.reduce((s,v) => s+v, 0) / vals.length) : null;
                  const c = cellColor(avg);
                  return <td key={i} style={{ padding:"8px 4px", textAlign:"center", borderTop:"2px solid #e2e8f0", fontWeight:700, fontSize:13, ...c }}>{avg != null ? `${avg}%` : "—"}</td>;
                })}
                {(() => {
                  const allYtd = employees.map(e => e.ytd_avg).filter(v => v > 0);
                  const oa = allYtd.length ? Math.round(allYtd.reduce((s,v) => s+v, 0) / allYtd.length) : null;
                  const c = cellColor(oa);
                  return <td style={{ padding:"8px 10px", textAlign:"center", borderTop:"2px solid #e2e8f0", fontWeight:700, fontSize:14, ...c }}>{oa != null ? `${oa}%` : "—"}</td>;
                })()}
              </tr>
            </tfoot>
          </table>
          <div style={{ display:"flex", gap:16, marginTop:12, fontSize:12, color:"#6b7280" }}>
            <span><span style={{ display:"inline-block", width:12, height:12, background:"#dcfce7", border:"1px solid #86efac", borderRadius:3, marginRight:4 }} />≥80% Good</span>
            <span><span style={{ display:"inline-block", width:12, height:12, background:"#fef9c3", border:"1px solid #fde047", borderRadius:3, marginRight:4 }} />50–79% Moderate</span>
            <span><span style={{ display:"inline-block", width:12, height:12, background:"#fee2e2", border:"1px solid #fca5a5", borderRadius:3, marginRight:4 }} />&lt;50% Low</span>
            <span><span style={{ display:"inline-block", width:12, height:12, background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:3, marginRight:4 }} />No data</span>
          </div>
        </div>
      )}
    </div>
  );
}
